/* tslint:disable:no-require-imports */
import cloneDeep from "lodash.clonedeep";
import {
    IEditorBackground,
    IEditorCanvas,
    IEditorElement, IEditorImage,
    IEditorPage, IEditorPhoto,
    IEditorShape, IEditorShapeAsset,
    IEditorText, IImagePosition, Imports, IThumbDictionary, IWebsiteImage, IWebsiteThumb
} from "./editor.interfaces";
import { EditorHelpers } from "./helpers";
import {IPhoto} from "@/customizer/interfaces";

    const globalImports: Imports = typeof global !== 'undefined' ? global as any : window as any;

    const fabric = globalImports.fabric;

    // @ts-ignore
    const SF: StorefrontEditorSF = globalImports.SF || {};
    SF.Util = SF.Util || {} as any;

    // tslint:disable-next-line:variable-name
    const Model = globalImports.SF.Model;
    const Editor = globalImports.SF.Editor;

    export class EditorTranslator  {
        private lockAcquired: boolean = false;
        private lockReleasedResolver: null|(() => void) = null;

        constructor(public saveTextPreview: boolean, public extraPropertiesToInclude: string[]) {

        }

        public boardSetter(board: IEditorCanvas, elementCollection: any, skipDefaultText: boolean): void {
            // default skip text to false
            skipDefaultText = skipDefaultText || false;

            // board.clear() removes overlayImage. Keep it here to add later...
            const overlayImage = board.overlayImage;

            // remove everything currently on the board
            board.clear();
            if (overlayImage) {
                board.overlayImage = overlayImage;
            }
            board.objectsToLoad = 0;

            // convert element to shapes
            let shapes: IEditorShape[] = [];
            const unEditableShapes: any[] = [];
            let xOffset = 0;

            for (let i = 0; i < elementCollection.pages.length; i++) {
                const page = elementCollection.pages[i];

                // if the page is un-editable, we add a white rectangle to cover any content that is spanning
                if (!page.canEdit) {
                    unEditableShapes.push(new fabric.Rect({
                        fill: '#ffffff',
                        left: xOffset + (page.width / 2),
                        top: page.height / 2,
                        width: page.width,
                        height: page.height,
                        selectable: false
                    }));
                }

                for (let j = 0; j < page.elements.length; j++) {
                    if (!fabric.isLikelyNode && (board as any).state) {
                        const currentProject = (board as any).state.currentProject;
                        if (page.elements[j].image && page.elements[j].image.id && currentProject.photos.expired &&
                            currentProject.photos.expired.indexOf(page.elements[j].image.id.toString()) >= 0) {
                            page.elements[j].image = null;
                        }

                    }
                    if (page.type == SF.Model.PageTypes.SPINE && page.elements[j].image && page.elements[j].image.type == 'background') {
                        page.elements[j].onSpine = true;
                    }
                    const shape = this.toShape(page.elements[j]);

                    // adjust offset
                    if ((shape as any).mainType != 'shapeAsset') {
                        shape.left += xOffset;
                    }

                    // if page is un-editable, force selection to false
                    if (!page.canEdit) {
                        shape.selectable = false;
                    }

                    if (page.canEdit) {
                        // add to array at specific index
                        // index: at page 0, its equivalent to j
                        //        on other pages, it offsets as such:
                        //          - on second page, first element is the second element, etc.
                        //          - on third page, first element is the third element, etc.
                        if (shape instanceof Editor.Background) {
                            let noBackgrounds: number = 0;
                            if ((page.type == 'front_cover' && !fabric.isLikelyNode) || (page.type == 'back_cover' && !(shape as IEditorBackground).onSpine && fabric.isLikelyNode)) {
                                let lastBackgroundIndex = 0;
                                for (let shapeIndex = 0; shapeIndex < shapes.length; shapeIndex++) {
                                    if (shapes[shapeIndex] instanceof Editor.Background) {
                                        lastBackgroundIndex = shapeIndex;
                                        noBackgrounds++;
                                    }
                                }
                                if (noBackgrounds > 0) {
                                    shapes.splice(lastBackgroundIndex + 1, 0, shape);
                                } else {
                                    shapes.unshift(shape);
                                }
                            } else {
                                shapes.unshift(shape);
                            }
                        } else {
                            shapes.splice(this.calculateDepth(i, j), 0, shape);
                        }
                    } else {
                        unEditableShapes.push(shape);
                    }

                }

                // deal with any bleed data
                if (page.bleed && page.bleed[Model.WrapOptions.EDGE_STYLE] && elementCollection.pages.length === 1) {
                    board.wrap = page.bleed;
                } else {
                    // @ts-ignore
                    board.wrap = null as any;
                }
                board.productType  = page.productType;

                if (page.bleed
                    && (page.bleed[SF.Model.CoverPhotoMarginAttribute.COVER_PHOTO_MARGIN_BOTTOM]
                        || page.bleed[SF.Model.CoverPhotoMarginAttribute.COVER_PHOTO_MARGIN_TOP]
                        || page.bleed[SF.Model.CoverPhotoMarginAttribute.COVER_PHOTO_MARGIN_LEFT]
                        || page.bleed[SF.Model.CoverPhotoMarginAttribute.COVER_PHOTO_MARGIN_RIGHT])) {
                    board.coverPageBleed = page.bleed;
                } else {
                    board.coverPageBleed = null as any;
                }

                // increment offset
                xOffset += page.width;
            }

            // we ensure that any content from un-editable pages has the highest z-index. This way, editable
            // content will fall underneath when it spans.
            shapes = shapes.concat(unEditableShapes);

            // if we need to filter out any text
            if (skipDefaultText) {
                // filter out unedited text boxes
                shapes = this.filterTexts(shapes, board);
            }

            // add shapes to board
            shapes.forEach((shape: IEditorShape): void => {
                let textAddedAsPreviewImage = false;
                if (shape.type == 'sf-text') {
                    if (!fabric.isLikelyNode) {
                        if (shape.isEditing) {
                            (shape as any).exitEditing();
                        }
                    } else if (shape.preview_file || shape.preview_image) {
                        if (this.addTextPreviewImageToNodeCanvas(shape, board)) {
                            textAddedAsPreviewImage = true;
                        }
                    }
                }
                const shapeAssetTypes = Object.keys(SF.Model.ShapeAssetTypes).map(
                    (key: string): string => {
                        return SF.Model.ShapeAssetTypes[key];
                    });

                if (((shape as any)).type && shapeAssetTypes.indexOf((shape as any).type) >= 0) {
                    fabric.util.enlivenObjects([shape], (objects: fabric.Object[]) => {
                        const liveObject = objects[0];
                        board.add(liveObject);
                    }, '');
                } else if (!textAddedAsPreviewImage) {
                    board.add(shape);
                }
            });

        }
        //todo use proper types instead of any
        // noinspection JSUnusedLocalSymbols
        protected setEmptyImageFillColor(options: any, clonedElement: any): void {
            // override if needed
        }

        protected filterTexts(shapes: IEditorShape[], board: IEditorCanvas): IEditorShape[] {
            // filter out unedited text boxes
            return shapes.filter((shape: IEditorShape): boolean => {
                /** default texts should be handled in a better way on jrs. There are default texts like personalized messages that we do not show
                 * on production image, however, there are default texts that need to be shown on production even if they are not edited by the user such as the
                 * ones we have on 4Ocean canvas products
                 */

                if (shape.type === 'sf-text') {
                    const text = shape as IEditorText;
                    if (fabric.isLikelyNode) {
                        for (const key in text.placeHolderTranslations) {
                            if (text.placeHolderTranslations.hasOwnProperty(key)) {
                                // if the text is a placeholder and it is not edited by the user, we do not show it on production image - if canEdit is
                                // false then it is not an editable text...
                                if (shape.text === text.placeHolderTranslations[key] && !text.addedByUser && !text.edited && text.canEdit) {
                                    return false;
                                }
                            }
                        }
                    } else if (text.placeHolderTranslations && shape.text == text.placeHolderTranslations[SF.currentLanguage] &&
                        !text.addedByUser && !text.edited) {
                        return false;
                    }

                    if (text.addedByUser && !text.edited) {
                        // Declare project as modified. Page previews need to be saved again to remove the unedited texts added originally by the user
                        // otherwise projectModified remains true and it never saves the modified preview after unedited text is removed when saving to cart
                        if (!fabric.isLikelyNode && (board as any).state) {
                            (board as any).state.projectModified = true;
                        }
                        return false;
                    }

                }
                return true;
            });
        }

        private addTextPreviewImageToNodeCanvas(shape: IEditorShape, board: any): boolean {
                const imageOptions = {
                    originX: 'center',
                    originY: 'center',
                    left: shape.left,
                    top: shape.top,
                };
            if (shape.preview_image) {
                const imageElement = new (globalThis as any).Image();
imageElement.src = 'data:image/png;base64,' + shape.preview_image;
                const fabricImage = new fabric.Image(imageElement, imageOptions);
                board.add(fabricImage);
                return true;
            } else if (shape.preview_file) {
                const simpleImage = new SF.Editor.SimpleImage(shape.preview_file, imageOptions);
                board.add(simpleImage);
                return true;
            }
            return false;
        }

        public cloneShape(shape: fabric.Object): IEditorShape {
            const element = this.toElement(shape);

            // since we are cloning, we need a new uid
            element.uid = element.generateUid();

            return this.toShape(element);
        }

        private calculateDepth(pageIndex: number, elementIndex: number): number {
            return ((elementIndex + 1) * pageIndex) + elementIndex;
        }

        /**
         * Calculates the element offset.
         */
        public calculateOffset(edits: { pages: IEditorPage[] }, pageIndex: number): number {
            let offset = 0;

            for (let i = 0; i < pageIndex; i++) {
                offset += edits.pages[i].width;
            }

            return offset;
        }

        public toShape(element: IEditorElement|IEditorShape): IEditorShape {
            // we clone the element here
            // note that all the instanceof checks are against element, because the prototype isn't carried through
            let clonedElement: any;
            if (element instanceof SF.Model.ShapeAsset) {
                clonedElement = (element as any).jsonData;
                clonedElement.mainType = (element as any).mainType;
                clonedElement.uid = element.uid;
                clonedElement.locked = element.locked;
                clonedElement.fixed = element.fixed;
                clonedElement.isLetterBoxed = element.isLetterBoxed;
                clonedElement.editable = element.editable;
                clonedElement.editableSave = element.editableSave;
                clonedElement.order = element.order;
                clonedElement.generateUid = element.generateUid;
                clonedElement.effects = clonedElement.effects ? fabric.util.object.clone(clonedElement.effects) : {};
            } else {
                clonedElement = element;
            }

            // TODO: Create type for options
            const options: any = {
                uid: clonedElement.uid,
                left: clonedElement.x,
                top: clonedElement.y,
                width: clonedElement.width,
                height: clonedElement.height,
                angle: clonedElement.rotation,
                opacity: clonedElement.opacity,
                effects: clonedElement.effects ? cloneDeep(clonedElement.effects) : {},
                locked: clonedElement.locked,
                fixed: clonedElement.fixed,
                editableSave: clonedElement.editableSave,
                isLetterBoxed: clonedElement.isLetterBoxed,

                selectable: clonedElement.editable,

                // data for flash pass-through
                order: clonedElement.order,
                uniquePhotoId: clonedElement.uniquePhotoId ? clonedElement.uniquePhotoId : undefined,
                pattern: element.pattern
            };

            this.extraPropertiesToInclude.forEach((extraPropertyToInclude: string) => {
                if (clonedElement[extraPropertyToInclude] !== undefined) {
                    Object.defineProperty(options, extraPropertyToInclude, {
                        value: clonedElement[extraPropertyToInclude],
                        writable: true,
                        enumerable: true,
                        configurable: true
                    });
                }
            });
            const effects = options.effects;

            if (effects) {
                if (effects.shadowAngle || effects.shadowOpacity) {
                    options.shadow = this.buildShadow(effects.shadowColor, effects.shadowOpacity, effects.shadowBlur, effects.shadowDistance, effects.shadowAngle, true);
                } else if (effects.glowOpacity || effects.glowBlur) {
                    options.shadow = this.buildGlow(effects.glowColor, effects.glowOpacity, effects.glowBlur, true);
                }
            }

            // if it's a text object, convert and return
            if (element instanceof Model.TextElement) {
                options.styles = clonedElement.styles;
                options.preview_image = clonedElement.preview_image;
                options.preview_file = clonedElement.preview_file;
                options.edited = clonedElement.edited;
                options.editable = clonedElement.editable;
                options.addedByUser = clonedElement.addedByUser;
                options.textBeforeEdit = clonedElement.textBeforeEdit;
                options.placeHolderTranslations = clonedElement.placeHolderTranslations;
                options.originalHeight = clonedElement.originalHeight;
                options.textZoneId = clonedElement.textZoneId;
                options.linkedZoneId = clonedElement.linkedZoneId;
                return new Editor.Text(clonedElement.text || '', globalImports.fabric.util.object.extend(options, clonedElement.rootStyle));
            }

            // if its a clipart, convert and return
            if (element instanceof Model.ClipartElement) {
                return new Editor.Clipart(clonedElement.image, options);
            }
            // if it's a shape asset
            if (element as any instanceof SF.Model.ShapeAsset) {
                return clonedElement;
            }
            const shapeAssetTypes = Object.keys(SF.Model.ShapeAssetTypes).map(
                function (key: string): string {
                    return SF.Model.ShapeAssetTypes[key];
                });
            if ((element as any).type && shapeAssetTypes.indexOf((element as any).type) >= 0) {
                return clonedElement;
            }

            if (element as any instanceof Model.GroupElement) {
                options.canvas = clonedElement._canvas;
                const innerObjects: fabric.Object[] = [];
                for (let i = 0; i < clonedElement._objects.length; i++) {
                    const innerObject = clonedElement._objects[i];
                    // if it's a shape
                    if (innerObject.mainType && innerObject.mainType == 'shapeAsset') {
                        const convertedShapeAsset = this.toShape(innerObject);
                        if (convertedShapeAsset instanceof fabric.Object) {
                            innerObjects.push(convertedShapeAsset);
                        } else {
                            fabric.util.enlivenObjects([convertedShapeAsset], (livenObjects: fabric.Object[]) => {
                                innerObjects.push(livenObjects[0]);
                            }, '');
                        }

                    } else if (!innerObject.type) {
                        innerObjects.push(this.toShape(innerObject));
                    } else {
                        innerObjects.push(innerObject);
                    }
                }
                const groupShape = new Editor.Group(innerObjects, options) as any;
                groupShape.subTargetCheck = true;
                return groupShape;
            }

            // photos and backgrounds require image position and frame position
            options.imagePosition = this.getElementImagePosition(clonedElement);
            if ((element as any).userZoom) {
                options.userZoom = (element as any).userZoom;
            }

            if (element as any instanceof Model.PhotoElement) {
                this.setEmptyImageFillColor(options, clonedElement);
                return new Editor.PhotoGroup(clonedElement.image, clonedElement.frame, options);
            }

            if (element as any instanceof Model.ForegroundElement) {
                return new Editor.Foreground(clonedElement.image, options);
            }
            options.onSpine = clonedElement.onSpine;
            return new Editor.Background(clonedElement.image, options);
        }

        private createPreviewImageFromText(textObject: fabric.Object): Promise<string> {
            return new Promise<string>((resolve, reject) => {

                this.waitForLock(2500).then(() => {

                    let virtual:HTMLCanvasElement|null = document.querySelector<HTMLCanvasElement>('#virtual');
                    if (!virtual) {
                        virtual = fabric.util.createCanvasElement() as HTMLCanvasElement;
                        virtual.id = 'virtual';
                        virtual.style.display = 'none';
                    }
                    const fabricCanvas = new fabric.Canvas(virtual);

                    const clip = {
                        left: 0,
                        top: 0,
                        width: 0,
                        height: 0
                    };

                    (fabricCanvas as any).fontService = (textObject as any).canvas.fontService;
                    const clonedText = (fabric.util.object as fabric.IUtilObject).clone(textObject);

                    clonedText.scaleX = textObject.scaleX || 0;
                    clonedText.scaleY = textObject.scaleY || 0;
                    if (textObject.canvas) {
                        const zoom = textObject.canvas.getZoom();
                        fabricCanvas.width = textObject.canvas.getWidth() / zoom;
                        fabricCanvas.height = textObject.canvas.getHeight() / zoom;
                    }
                    //    Next line was originally added to fix PS-59403, however if we have carriage returns between lines
                    //    it results in incorrect line height calculation and texts on jrs will show up with extra spaces between lines.
                    //    We should find a different solution for ps-59403
                    //    clonedText._fontSizeMult = 1.35;

                    fabricCanvas.add(clonedText);

                    const shadowOffset = { x: 0, y: 0 };
                    const blurOffset = { x: 0, y: 0 };
                    if (clonedText.shadow) {
                        shadowOffset.x = clonedText.shadow.offsetX;
                        shadowOffset.y = clonedText.shadow.offsetY;
                        if (shadowOffset.x === 0 && shadowOffset.y === 0 && clonedText.shadow.blur) {
                            blurOffset.x = clonedText.shadow.blur * clonedText.scaleX;
                            blurOffset.y = clonedText.shadow.blur * clonedText.scaleY;
                        }
                    }

                    const extraSpaceAroundText = clonedText.getBoundingRect().height; //adding extra space as big as the bounding size height around the text box screenshot to capture the full text height
                    const offsetTop = Math.abs(shadowOffset.y * clonedText.scaleY);
                    const offsetLeft = Math.abs(shadowOffset.x * clonedText.scaleX);
                    const boundingBox = clonedText.getBoundingRect(undefined, true); // recalculate bounding box
                    const boundingBoxWidth = boundingBox.width;
                    const boundingBoxHeight = boundingBox.height;
                    const boundingBoxLeft = boundingBox.left;
                    const boundingBoxTop = boundingBox.top;

                    clip.left = boundingBoxLeft - offsetLeft - blurOffset.x;
                    clip.top = boundingBoxTop - offsetTop - blurOffset.y - extraSpaceAroundText;
                    clip.width = boundingBoxWidth + 2 * offsetLeft + 2 * blurOffset.x;
                    clip.height = boundingBoxHeight + 2 * offsetTop + 2 * blurOffset.y + 2 * extraSpaceAroundText;

                    let failedInterval = setTimeout(() => {
                        fabricCanvas.off('after:render', afterRender);
                        // @ts-ignore
                        failedInterval = 0;
                        reject('Failed to render text preview');
                    }, 2000);

                    const afterRender =  () => {
                        if (failedInterval) {
                            const objectsToLoad = fabricCanvas.getObjects().reduce((accum: number, obj: any) => {
                                return accum + ((obj.hasOwnProperty('ready') && !obj.ready) ? 1 : 0);
                            }, 0);
                            console.log('After render ' + failedInterval + ', objects to load: ' + objectsToLoad);
                            if (objectsToLoad === 0) {
                                console.log('Render finished');
                                fabricCanvas.off('after:render', afterRender);
                                clearInterval(failedInterval);
                                // @ts-ignore
                                failedInterval = 0;
                                this.releaseLock();
                                const format = EditorHelpers.supportsWebp() ? 'webp' : 'png';
                                const newDataUrl = fabricCanvas.toDataURL({
                                    format,
                                    left: clip.left,
                                    top: clip.top,
                                    width: clip.width,
                                    height: clip.height
                                });
                                resolve(newDataUrl);
                            }
                        }
                    };

                    fabricCanvas.on('after:render', afterRender);
                    fabricCanvas.renderAll();
                }, err => {
                    this.releaseLock();
                    reject(err);
                });

            });
        }

        public getPreviewImageFromTextAsBase64(textObject: fabric.Object): Promise<string> {
            const maxRetries = 3;

            const attemptPreviewCreation = (retryCount: number): Promise<string> => {
                return new Promise((resolve, reject) => {
                    if (textObject.canvas) {
                        this.createPreviewImageFromText(textObject)
                            .then(previewImage => {
                                const result = previewImage.replace(/^data:image\/(png|jpe?g|webp);base64,/, '');
                                if (result.length < 10) {
                                    reject('Error creating text preview.');
                                } else {
                                    resolve(result);
                                }
                            })
                            .catch(error => {
                                if (retryCount < maxRetries) {
                                    console.warn(`Attempt ${retryCount + 1} failed ${JSON.stringify(error)}. Retrying...`);
                                    return resolve(attemptPreviewCreation(retryCount + 1)); // Retry
                                } else {
                                    reject(`Failed after ${maxRetries} attempts: ${JSON.stringify(error)}`);
                                }
                            });
                    } else {
                        reject('Invalid textObject, no canvas');
                    }
                });
            };

            return attemptPreviewCreation(0);
        }

        public setSaveTextPreview(saveTextPreview: boolean): void {
            this.saveTextPreview = saveTextPreview;
        }

        public resetSaveTextPreview(): void {
            this.saveTextPreview = false;
        }

        public getSaveTextPreview(): boolean {
            return this.saveTextPreview;
        }

        public getElementImagePosition(el: IEditorElement, forceRecalculate?: boolean): IImagePosition {

            const element = el as any;
            if (element.imagePosition && !forceRecalculate) {
                return fabric.util.object.clone(element.imagePosition);
            }
            return this.getImagePosition(element.width as number, element.height as number,
                element.getMarginLeft ? element.getMarginLeft() : 0, element.getMarginTop ? element.getMarginTop() : 0,
                element.getMarginRight ? element.getMarginRight() : 0, element.getMarginBottom ? element.getMarginBottom() : 0,
                element.image,element.effects ? element.effects.rotation : 0);
        }

        public getImagePosition(width: number, height: number, marginLeft: number, marginTop: number, marginRight: number,
                                marginBottom: number, image: IEditorImage, rotation: number): IImagePosition {
            if (!image) {
                return this.generateImagePosition(width / -2, height / -2, width, height, 1);
            }

            let frameWidth = width - marginLeft - marginRight;
            let frameHeight = height - marginTop - marginBottom;

            // if there is an image, figure out the placement
            if (rotation) {
                const box = EditorHelpers.boundingBox(rotation, width, height, 0, 0, marginLeft, marginTop, marginRight, marginBottom);

                frameWidth = box[1] - box[0];
                frameHeight = box[3] - box[2];
            }

            // calculate zoom
            const zoom = 1 / EditorHelpers.zoomToFit(frameWidth, frameHeight, image.width, image.height);

            // calculate crop rect (only if there is a rotation
            const scaledWidth = image.width * zoom;
            const scaledHeight = image.height * zoom;
            const cropRect = image instanceof Model.Photo && !rotation ? (image as IEditorPhoto).getBestCropRectangle(scaledWidth, scaledHeight) : null;

            if (cropRect) {
                const frameWindowX = (width / 2) - marginLeft;
                const frameWindowY = (height / 2) - marginTop;

                // maxima for width
                if (cropRect[0] + frameWindowX > scaledWidth) {
                    cropRect[0] = scaledWidth - frameWindowX;
                } else if (cropRect[0] < frameWindowX) {
                    cropRect[0] = frameWindowX;
                }

                // maxima for height
                if (cropRect[1] + frameWindowY > scaledHeight) {
                    cropRect[1] = scaledHeight - frameWindowY;
                } else {
                    cropRect[1] = frameWindowY;
                }

                return this.generateImagePosition(-cropRect[0], -cropRect[1], image.width, image.height, zoom);
            }

            return this.generateImagePosition(scaledWidth / -2, scaledHeight / -2, image.width, image.height, zoom);
        }

        private generateImagePosition(x: number, y: number, width: number, height: number, zoom: number): IImagePosition {
            return {x: x, y: y, width: width, height: height, zoom: zoom};
        }

        public toElement(shapeObject: fabric.Object): IEditorElement {
            const propertiesToInclude: any[] = ['fixed', 'order'];
            if (this.extraPropertiesToInclude.length > 0) {
                propertiesToInclude.push(...this.extraPropertiesToInclude);
            }
            const object = shapeObject.toObject(propertiesToInclude);

            let element: any;

            const shapeAssetTypes = Object.keys(SF.Model.ShapeAssetTypes).map((key: string): string => {
                return SF.Model.ShapeAssetTypes[key];
            });

            // noinspection SuspiciousInstanceOfGuard
            if (shapeObject instanceof Editor.Text) {
                const styles = object.styles;
                const shape = shapeObject as IEditorText;
                // generate a root styles object
                const rootStyles = {
                    fill: object.fill,
                    fontFamily: object.fontFamily,
                    fontSize: object.fontSize,
                    fontStyle: object.fontStyle,
                    fontWeight: object.fontWeight,
                    textAlign: object.textAlign,
                    backgroundColor: object.backgroundColor,
                    underline: object.underline,
                    overline: object.overline,
                    linethrough: object.linethrough,
                    autoFontSize: shape.autoFontSize,
                    textDecoration: shape.textDecoration,
                    textTransform: shape.textTransform,
                    maxCharacters: shape.maxCharacters,
                    lockFontColor: shape.lockFontColor,
                    lockFontSize: shape.lockFontSize,
                    lockTextAlign: shape.lockTextAlign,
                    lockTextStyle: shape.lockTextStyle,

                };

                element = new Model.TextElement(object.text, rootStyles, styles);
                element.preview_image = (shapeObject as any).preview_image;
                element.preview_file = (shapeObject as any).preview_file;
                if (this.saveTextPreview) {
                    this.getPreviewImageFromTextAsBase64(shape).then(dataUrl => {
                        element.preview_image = dataUrl;
                    });
                }
                element.edited = (shape as any).edited;
                element.editable = (shape as any).editable ||  (shape as any).linkEdited;
                element.addedByUser = (shape as any).addedByUser;
                element.textBeforeEdit = (shape as any).textBeforeEdit;
                element.placeHolderTranslations = (shape as any).placeHolderTranslations;
                element.originalHeight = (shape as any).originalHeight;

            } else if (shapeObject instanceof Editor.Clipart) {
                element = new Model.ClipartElement(object.image);
            } else if (shapeObject instanceof Editor.PhotoGroup) {
                const image = object.photo;
                const imagePosition = (fabric.util.object as fabric.IUtilObject).clone(object.imagePosition);

                // noinspection SuspiciousInstanceOfGuard
                if (shapeObject instanceof Editor.Background) {
                    element = new Model.BackgroundElement(image, imagePosition);
                    element.onSpine = (shapeObject as IEditorBackground).onSpine;
                } else if ((shapeObject as any) instanceof Editor.Foreground) {
                    element = new Model.ForegroundElement(image, imagePosition);
                } else {
                    element = new Model.PhotoElement(image, imagePosition, object.frame);
                }
            } else if (shapeObject.type == 'group') { // if it is a group
                element = new Model.GroupElement(object, (shapeObject as any)._objects, (shapeObject as any).canvas);
                if ((shapeObject as any).uid) {
                    element.uid = (shapeObject as any).uid;
                }
                return element;
            } else if (shapeObject.type !== undefined && shapeAssetTypes.indexOf(shapeObject.type) >= 0) { // if it is a shape asset
                element = object;
                element.uid = (shapeObject as any).uid;
                element.mainType = (shapeObject as any).mainType;
                element.x = object.left;
                element.y = object.top;
                element.width = object.width;
                element.height = object.height;
                element.editable = shapeObject.selectable;
                element.editableSave = (shapeObject as any).editableSave;
                element.locked = object.locked;
                element.fixed = object.fixed;
                element.isLetterBoxed = object.isLetterBoxed;
                element.rotation = object.angle;
                element.opacity = object.opacity;
                element.effects = (shapeObject as any).effects ? fabric.util.object.clone((shapeObject as any).effects) : {};
                element.order = object.order;
                element.generateUid = (): string => {
                    return this.generateUid();
                };
                return element;
            }

            if (element) {
                // default options
                element.uid = (shapeObject as any).uid;
                element.x = object.left;
                element.y = object.top;
                element.width = object.width;
                element.height = object.height;
                element.editable = shapeObject.selectable;
                element.editableSave = (shapeObject as any).editableSave;
                element.locked = object.locked;
                element.fixed = object.fixed;
                element.isLetterBoxed = object.isLetterBoxed;
                element.pattern = object.pattern;

                element.rotation = object.angle;
                element.opacity = object.opacity;
                element.effects = object.effects ? cloneDeep(object.effects) : {};

                // data for flash pass-through
                element.order = object.order;

                this.extraPropertiesToInclude.forEach((extraPropertyToInclude: string) => {
                    if (object[extraPropertyToInclude] !== undefined) {
                        Object.defineProperty(element, extraPropertyToInclude, {
                            value: object[extraPropertyToInclude],
                            writable: true,
                            enumerable: true,
                            configurable: true
                        });
                    }
                });

                if ((shapeObject as any).uniquePhotoId) {
                    element.uniquePhotoId = (shapeObject as any).uniquePhotoId;
                }
                if ((shapeObject as any).userZoom) {
                    element.userZoom = (shapeObject as any).userZoom;
                }
            }
            return element as IEditorElement;
        }

        public generateUid(len: number = 7): string {
            return Math.random().toString(35).substring(2, len + 2);
        }

        public toImageModel(photo: IWebsiteImage|IPhoto, type?: string): IEditorImage {
            type = type || 'photo';

            if (type === 'photo') {
                return new Model.Photo(parseInt(photo.id || '0', 10), this.getUrlPrefix(photo.full_url as string),
                    this.generateThumbsObject(photo as IWebsiteImage), photo.image_data, photo.edits);

            }

            return new Model.GraphicAsset(parseInt(photo.id || '0', 10), type, this.getUrlPrefix(photo.full_url as string), this.generateThumbsObject(photo as IWebsiteImage));
        }

        public convertAssetIntoEditorModel(asset: any): IEditorImage {
            switch (asset.type) {
                case 'template':
                    return new SF.Model.Template(asset.id, asset.category_id);

                case 'layout':
                    return new SF.Model.Layout(asset.id, asset.category_id, asset.frames, asset.layout);

                case 'edge':
                    return new SF.Model.Edge(
                        asset.id,
                        this.getUrlPrefix(asset.thumb_url || asset.src),
                        this.generateThumbsObjectFromAsset(asset),
                        asset.margin_top,
                        asset.margin_right,
                        asset.margin_bottom,
                        asset.margin_left
                    );
                case 'shape':
                    return this.convertToFabricShapeObject(asset) as any;

                default:
                    return this.convertToImageObject(asset);
            }
        }

        private convertToImageObject(asset: any) {
            return new SF.Model.GraphicAsset(
                asset.id,
                asset.type,
                this.getUrlPrefix(asset.thumb_url),
                this.generateThumbsObjectFromAsset(asset)
            );
        }

        private convertToFabricShapeObject(asset: any): IEditorShapeAsset {
            return new SF.Model.ShapeAsset(asset.jsonData);
        }

        private generateThumbsObjectFromAsset(asset: any): IThumbDictionary {
            const thumbsDictionary: IThumbDictionary = {};

            // first, add the full size
            thumbsDictionary[SF.Model.ThumbSize.FULL] = new SF.Model.Thumb(this.getImageName(asset.filename), asset.width, asset.height);
            thumbsDictionary[SF.Model.ThumbSize.SMALL] = new SF.Model.Thumb(this.getImageName(asset.thumb_filename), asset.thumb_width, asset.thumb_height);
            thumbsDictionary[SF.Model.ThumbSize.MEDIUM] = new SF.Model.Thumb(this.getImageName(asset.medium_thumb_filename), asset.medium_thumb_width, asset.medium_thumb_height);

            // not all assets have a large thumb
            if (asset.large_thumb_filename) {
                thumbsDictionary[SF.Model.ThumbSize.LARGE] = new SF.Model.Thumb(this.getImageName(asset.large_thumb_filename), asset.large_thumb_width, asset.large_thumb_height);
            }

            return thumbsDictionary;
        }

        private isDataUrl(path: string):boolean {
            return path.substring(0, 4) === 'data';
        }

        private getUrlPrefix(path: string): string {
            return this.isDataUrl(path) ? '' : path.substring(0, path.lastIndexOf('/') + 1);
        }

        private getImageName(path: string): string {
            return this.isDataUrl(path) ? path : path.substring(path.lastIndexOf('/') + 1);
        }

        private generateThumbsObject(photo: IWebsiteImage): IThumbDictionary {
            const thumbsDictionary: IThumbDictionary = {};
            const thumbs: IWebsiteThumb[] = photo.thumb_list;

            // first, add the full size
            // hack: when the image is rotated, the width and height of only the full image is flipped.. deal with it here
            const flipped = Math.abs(photo.edits.rotation) % 180 === 90;
            const fullWidth = flipped ? photo.height : photo.width;
            const fullHeight = flipped ? photo.width : photo.height;
            thumbsDictionary[Model.ThumbSize.FULL] = new Model.Thumb(this.getImageName(photo.full_url), fullWidth, fullHeight);

            // now, iterate through thumb list and get the rest of them
            for (let i = 0; i < thumbs.length; i++) {
                thumbsDictionary[Model.ThumbSize.mapFromPhp(thumbs[i].thumb_type)] = new Model.Thumb(this.getImageName(thumbs[i].url), thumbs[i].width, thumbs[i].height);
            }

            return thumbsDictionary;
        }

        public buildShadow(color: string, opacity: number, blur: number, distance: number, angle: number, strict?: boolean): fabric.Shadow | null {
            if (strict && !angle && !blur && !distance) {
                return null;
            }
            color = color || 'rgba(0,0,0,0)';
            opacity = opacity || 0;
            if (!angle) {
                angle = 180;
            }
            angle = -((180 - angle) * Math.PI / 180);
            distance = distance || 0;
            if (color.indexOf('rgb') >= 0) {
                // convert from rgb to hex and keep opacity value
                const rgbaColor = color;
                color = this.rgb2hex(rgbaColor);
                opacity = this.getOpacityFromRGBA(rgbaColor) * 100;
            }

            return new fabric.Shadow({
                color: this.buildRGBA(color, opacity),
                blur: blur,
                offsetX: Math.round(Math.cos(angle) * distance),
                offsetY: Math.round(Math.sin(angle) * distance)
            });
        }

        public buildGlow(color: string, opacity: number, blur: number, strict?: boolean): fabric.Shadow | null {
            if (strict && !opacity && !blur) {
                return null;
            }

            // If colour is specified, ensure blur and opacity are specified as in applyGlow01
            if (color) {
                blur = blur || 30;
                if (opacity != 0) {
                    opacity = opacity || 100;
                }
            }

            color = color || 'rgba(0,0,0,0)';
            opacity = opacity || 0;
            if (color.indexOf('rgb') >= 0) {
                // convert from rgb to hex and keep opacity value
                const rgbaColor = color;
                color = this.rgb2hex(rgbaColor);
                opacity = this.getOpacityFromRGBA(rgbaColor) * 100;
            }
            return new fabric.Shadow({
                color: this.buildRGBA(color, opacity),
                // color: color,
                blur: blur,
                offsetX: 0,
                offsetY: 0
            });
        }

        public buildRGBA(color: string, opacity: number): string {
            return 'rgba(' + this.hexToR(color) + ',' + this.hexToG(color) + ',' + this.hexToB(color) + ',' + (opacity / 100) + ')';
        }

        private hexToR(h: string): number {
            return parseInt((this.cutHex(h)).substring(0, 2), 16);
        }

        private hexToG(h: string): number {
            return parseInt((this.cutHex(h)).substring(2, 4), 16);
        }

        private hexToB(h: string): number {
            return parseInt((this.cutHex(h)).substring(4, 6), 16);
        }

        private cutHex(h: string): string {
            return (h.charAt(0) == '#') ? h.substring(1, 7) : h;
        }

        public rgb2hex(rgbValue: string): string {
            const rgb = rgbValue.match(/^rgba?[\s+]?\([\s+]?(\d+)[\s+]?,[\s+]?(\d+)[\s+]?,[\s+]?(\d+)[\s+]?/i);
            return (rgb && rgb.length === 4) ? '#' +
                ('0' + parseInt(rgb[1], 10).toString(16)).slice(-2) +
                ('0' + parseInt(rgb[2], 10).toString(16)).slice(-2) +
                ('0' + parseInt(rgb[3], 10).toString(16)).slice(-2) : '';
        }

        public getOpacityFromRGBA(rgba: string): number {
            return +(rgba.replace(/ /g, '').replace(/^.*,(.+)\)/, '$1'));
        }

        public isValidHex(color: string|number, checkLength: boolean): boolean {
            if (checkLength &&  (typeof color !== 'string' || (color.length !== 7 && checkLength && color.length !== 9))) {
                return false;
            }
            return typeof color === 'string' && /(^#[0-9A-F]{6}$)|(^#[0-9A-F]{3}$)|(^#[0-9A-F]{8}$)/i.test(color);
        }

        public getHexColor(color: string): string {
            return color.startsWith('rgb') ? this.rgb2hex(color) : color;
        }

        // Waits for the lock to be released, if it is locked, and when the lock is release it locks it.
        private acquireLock():Promise<void> {
            return new Promise<void>(resolve => {
                if (!this.lockAcquired) {
                    this.lockAcquired = true;
                    resolve();
                } else {
                    this.lockReleasedResolver = () => {
                        this.lockAcquired = true;
                        resolve();
                    };
                }
            });
        }

        private releaseLock():void {
            if (this.lockAcquired) {
                this.lockAcquired = false;
                if (this.lockReleasedResolver) {
                    this.lockReleasedResolver();
                    this.lockReleasedResolver = null;
                }
            }
        }

        private waitForLock(timeout: number): Promise<void> {
            return new Promise<void>((resolve, reject) => {
                const timeoutId = setTimeout(() => {
                    reject(new Error('Timeout waiting for lock'));
                }, timeout);

                this.acquireLock()
                    .then(() => {
                        clearTimeout(timeoutId);
                        resolve();
                    })
                    .catch(reject);
            });
        }

    }

    SF.Util.Translator = new EditorTranslator(false, []);

